import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NISCustonWrenchIcon from "@tCentro/Icons/NISCustonWrenchIcon";
import { NISCO } from "@tCentro/theme/design";
import NISProductListActions from "@tCentro/Widgets/ProductList/NISProductListActions";
import NISProductListProductImage, {
    NISProductListProductImageStyles,
} from "@tCentro/Widgets/ProductList/NISProductListProductImage";
import NISProductListProductInformation from "@tCentro/Widgets/ProductList/NISProductListProductInformation";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";

interface OwnProps {
    product: ProductModel;
    cardId: string;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        websiteSettings: { includeRealTimeInventoryAndPricingForSeo },
        productSettings: { productLoadStyle },
    } = getSettingsCollection(state);

    return {
        productInfo: getProductInfoFromList(state, ownProps.cardId, ownProps.product.id),
        includeRealTimeInventoryAndPricingForSeo,
        productLoadStyle,
    };
};
const mapDispatchToProps = {
    updateProductInfo,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface BrandProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    priceCartWrapper?: InjectableCss;
    compareWrapper?: InjectableCss;
    moreOptionsStyles?: InjectableCss;
    wrenchIconStyles?: IconPresentationProps;
    configureProductPlpStyles?: InjectableCss;
    imgStyles?: NISProductListProductImageStyles;
}

export const brandProductListProductCardStyles: BrandProductListProductCardStyles = {
    imgStyles: {
        wrapper: {
            css: css`
                > a {
                    justify-content: center;
                    align-items: center;
                }
            `,
        },
    },
    compareWrapper: {
        css: css`
            div[data-test-selector^="NISProductListProductCompare"] {
                width: 100%;
                div {
                    &:last-child {
                        justify-content: flex-end;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                null,
                                null,
                                css`
                                    justify-content: flex-end;
                                `,
                                css`
                                    justify-content: flex-end;
                                `,
                                css`
                                    justify-content: flex-end;
                                `,
                            ])}
                    }
                }
            }
        `,
    },
    moreOptionsStyles: {
        css: css`
            margin-bottom: 8px;
            border: 2px solid ${NISCO.COLORS.primaryColor};
            padding: 4px 12px 4px 8px;
            border-radius: 16px;
            font-family: ${NISCO.TYPOGRAPHY.fontFamily.base};
            span {
                font-family: ${NISCO.TYPOGRAPHY.fontFamily.base};
                font-size: 12px;
                font-weight: 600;
                line-height: 16px;
                color: ${NISCO.COLORS.primaryColor} !important;
                display: flex;
                align-items: center;
                gap: 2px;
            }
        `,
    },
    configureProductPlpStyles: {
        css: css`
            margin-top: 52px;
            margin-bottom: 12px;
            font-family: Poppins;
            font-size: 18px;
            font-weight: 600;
            line-height: 24px;
            color: ${NISCO.COLORS.primaryColor};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        margin-top: 60px;
                        margin-bottom: 24px;
                    `,
                    null,
                    null,
                ])}
            @media (min-width: 992px) and (max-width: 1181px) {
                margin-top: 60px !important;
                margin-bottom: 24px !important;
            }
            @media (min-width: 1517px) {
                margin-top: 60px !important;
                margin-bottom: 24px !important;
            }
        `,
    },
    wrenchIconStyles: {
        size: 16,
    },
    gridContainer: {
        gap: 0,
        css: css`
            padding: 16px;
            background: #ffffff;
            border: 1px solid #e6e6e6;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            transition: all 0.2s ease-in-out;
            height: 100%;

            &:hover {
                box-shadow: 0 6px 12px rgba(0, 0, 0, 0.12);
                transform: translateY(-2px);
            }
        `,
    },

    wrapper: {
        css: css`
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 20px;
            justify-content: center;
            align-items: stretch;
            width: 100%;
        `,
    },

    leftColumnGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 10px;
            img {
                max-height: 160px;
                object-fit: contain;
            }
        `,
    },

    rightColumnGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            flex: 1;
        `,
    },

    rightColumnGridContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            height: 100%;
        `,
    },

    infoGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            div[class^="GridItemStyle"] {
                padding: 0 !important;
            }

            .product-brand {
                font-size: 13px;
                font-weight: 600;
                color: #333;
            }

            .product-title {
                font-size: 15px;
                font-weight: 500;
                line-height: 20px;
                color: #222;
                margin-top: 4px;
                min-height: 40px;
            }

            .product-partnumbers {
                font-size: 13px;
                color: #777;
            }

            .product-price {
                font-weight: 700;
                font-size: 18px;
                color: #1a1a1a;
                margin-top: 8px;
            }

            .product-availability {
                margin-top: 8px;
                padding: 6px 10px;
                border-radius: 4px;
                font-size: 13px;
                display: inline-block;
            }

            .product-availability.instock {
                background: #e8f5e9;
                color: #1a7f37;
            }

            .product-availability.lowstock {
                background: #fff4e5;
                color: #b26a00;
            }

            .product-availability.outofstock {
                background: #fdecea;
                color: #b00020;
            }
        `,
    },

    actionsGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            margin-top: auto;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            gap: 8px;
            padding-top: 8px;
            border-top: 1px solid #eee;
        `,
    },

    priceCartWrapper: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
        `,
    },

    searchScoreDataGridItem: {
        width: 12,
        css: css`
            margin-top: 10px;
        `,
    },

    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            margin-bottom: 4px;
        `,
    },
};

const styles = brandProductListProductCardStyles;

const NISBrandTopCategoryProductsGridCard: FC<Props> = ({
    product,
    productInfo,
    updateProductInfo,
    cardId,
    includeRealTimeInventoryAndPricingForSeo,
}) => {
    if (!product || !productInfo) {
        return null;
    }
    const isCrawler = getIsWebCrawler();
    const showAvailability = !(isCrawler && !includeRealTimeInventoryAndPricingForSeo);
    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: cardId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: cardId, productId: product.id, unitOfMeasure });
    };
    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };
    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper>
                <StyledWrapper>
                    {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                    <NISProductListProductImage
                        showImage={true}
                        showCompare={false}
                        showImageBadge={false}
                        showTextBadge={false}
                        extendedStyles={styles.imgStyles}
                    />
                </StyledWrapper>
                <Hidden
                    below="md"
                    css={css`
                        display: flex;
                        justify-content: flex-start;
                    `}
                >
                    {product?.isVariantParent && (
                        <Link href={product?.canonicalUrl} {...styles.moreOptionsStyles}>
                            <Icon src={NISCustonWrenchIcon} {...styles.wrenchIconStyles} />
                            {translate("Configure Product")}
                        </Link>
                    )}
                </Hidden>
                <StyledWrapper>
                    <NISProductListProductInformation
                        showAttributes={false}
                        showBrand={true}
                        showPartNumbers={true}
                        showTitle={true}
                        showImageBadges={false}
                        maxImageBadges={4}
                        showTextBadges={false}
                        maxTextBadges={4}
                    />
                </StyledWrapper>
                <Hidden below="md">
                    {product?.isVariantParent && (
                        <Typography as="p" {...styles.configureProductPlpStyles}>
                            {siteMessage("configureProduct_Plp_Msg")}
                        </Typography>
                    )}
                </Hidden>
            </StyledWrapper>
            <StyledWrapper style={{ fontFamily: "Roboto" }}>
                <NISProductListActions showPrice={true} showAvailability={showAvailability} />
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(NISBrandTopCategoryProductsGridCard);
